import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, Smartphone } from "lucide-react";

const MODES = [
  ["off", "Off", "No one is asked for a code unless they've already set one up."],
  ["pilot", "Pilot group", "Only the staff ticked below must use an authenticator on web sign-in."],
  ["all", "Everyone", "Every web sign-in needs an authenticator code."],
];

export function WebMfaSettings({ users }) {
  const [mode, setMode] = useState(null);
  const [pilot, setPilot] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/web-mfa").then((r) => { setMode(r.data.mode); setPilot(r.data.pilot.map((p) => p.id)); })
      .catch(() => setMode(false));
  }, []);

  if (mode === false) return null;
  if (mode === null) return <div className="p-4"><Loader2 className="w-4 h-4 animate-spin text-indigo-500" /></div>;

  const toggle = (id) => setPilot((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const save = async () => {
    if (mode === "all" && !window.confirm("Every staff member will need an authenticator at their next web sign-in. Continue?")) return;
    setBusy(true);
    try { await api.put("/admin/web-mfa", { mode, pilot_user_ids: pilot }); toast.success("Authenticator settings saved"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save"); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 mb-5" data-testid="web-mfa-settings">
      <div className="flex items-center gap-2 mb-1"><Smartphone className="w-4 h-4 text-indigo-600" /><h2 className="text-sm font-bold text-slate-900">Web authenticator sign-in</h2></div>
      <p className="text-xs text-slate-500 mb-4">Applies to web sign-in only. The TedOps Field mobile app keeps signing in with a password, limited to its own screens.</p>
      <div className="grid sm:grid-cols-3 gap-2 mb-4">
        {MODES.map(([k, l, d]) => (
          <button key={k} type="button" onClick={() => setMode(k)} data-testid={`web-mfa-mode-${k}`}
            className={`text-left rounded-xl border p-3 transition-colors ${mode === k ? "border-indigo-500 bg-indigo-50" : "border-slate-200 hover:bg-slate-50"}`}>
            <div className="text-sm font-semibold text-slate-900">{l}</div><div className="text-xs text-slate-500">{d}</div>
          </button>
        ))}
      </div>
      {mode === "pilot" && (
        <div className="max-h-48 overflow-y-auto rounded-xl border border-slate-200 divide-y divide-slate-100 mb-4" data-testid="web-mfa-pilot-list">
          {users.filter((u) => u.active).map((u) => (
            <label key={u.id} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
              <input type="checkbox" checked={pilot.includes(u.id)} onChange={() => toggle(u.id)} data-testid={`web-mfa-pilot-${u.email}`} />
              <span className="text-slate-800">{u.name || u.email}</span><span className="text-xs text-slate-400">{u.email}</span>
            </label>
          ))}
        </div>
      )}
      <Button onClick={save} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="web-mfa-save">
        {busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Save
      </Button>
    </div>
  );
}
